/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import {
  cssMediaQueryMinWidthTablet,
  radius,
  Skeleton,
  spacing,
} from "@packages/ui";

type LineWidth = "short" | "medium" | "long";

const PARAGRAPH_PATTERNS: LineWidth[][] = [
  ["long", "medium", "long", "short"],
  ["medium", "long", "short", "medium"],
  ["long", "long", "medium"],
  ["short", "medium", "long", "short"],
  ["medium", "long", "medium"],
  ["long", "short", "medium", "long"],
];

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  height: "100vh",
  padding: `76px ${spacing.l} ${spacing.mediaPlayerMobile}`,
  [cssMediaQueryMinWidthTablet]: {
    padding: `144px ${spacing.l} ${spacing.mediaPlayerDesktop}`,
  },
};
const headerSkeletonStyle: Interpolation = {
  borderRadius: radius.m,
  flexShrink: 0,
  height: "80px",
  marginBottom: spacing.m,
  width: "80px",
};
const titleSkeletonStyle: Interpolation = {
  flexShrink: 0,
  height: "32px",
  marginBottom: spacing.l,
  width: "280px",
};
const contentContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  maxWidth: "640px",
  width: "100%",
};
const paragraphContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};
const lineSkeletonStyle: Interpolation = {
  borderRadius: spacing.xs,
  height: "24px",
};

const paragraphBreakStyle: Interpolation = {
  height: spacing.m,
};

const paragraphStyles: Record<LineWidth, Interpolation> = {
  short: {
    width: "65%",
  },
  medium: {
    width: "85%",
  },
  long: {
    width: "100%",
  },
};

const SkeletonLine = ({ width }: { width: LineWidth }) => (
  <Skeleton css={[lineSkeletonStyle, paragraphStyles[width]]} />
);

const ParagraphBreak = () => <div css={paragraphBreakStyle} />;

export const TranscriptSkeleton = () => {
  return (
    <div css={containerStyle}>
      <Skeleton css={headerSkeletonStyle} />
      <Skeleton css={titleSkeletonStyle} />

      <div css={contentContainerStyle}>
        {PARAGRAPH_PATTERNS.map((pattern, paragraphIndex) => (
          <div css={paragraphContainerStyle} key={paragraphIndex}>
            {pattern.map((width, lineIndex) => (
              <SkeletonLine key={lineIndex} width={width} />
            ))}
            {paragraphIndex < PARAGRAPH_PATTERNS.length - 1 && (
              <ParagraphBreak />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
